/**
 * Servicio de Depósitos
 * Maneja operaciones de depósitos de cheques
 */
import api from './api';

/**
 * Obtener todos los depósitos con filtros opcionales
 * @param {Object} filters - Filtros de búsqueda (fechaDesde, fechaHasta, cuentaId, estado)
 * @returns {Promise<Array>} Lista de depósitos
 */
export const getAllDeposits = async (filters = {}) => {
  try {
    const response = await api.get('/depositos', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener depósitos:', error);
    throw error;
  }
};

/**
 * Obtener un depósito por ID
 * @param {string} id - ID del depósito
 * @returns {Promise<Object>} Datos del depósito
 */
export const getDepositById = async (id) => {
  try {
    const response = await api.get(`/depositos/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener depósito:', error);
    throw error;
  }
};

/**
 * Crear un nuevo depósito
 * @param {Object} data - Datos del depósito (cuentaBancariaId, cheques[], fechaDeposito, etc.)
 * @returns {Promise<Object>} Depósito creado
 */
export const createDeposit = async (data) => {
  try {
    const response = await api.post('/depositos', data);
    return response.data;
  } catch (error) {
    console.error('Error al crear depósito:', error);
    throw error;
  }
};

/**
 * Actualizar un depósito existente
 * @param {string} id - ID del depósito
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Depósito actualizado
 */
export const updateDeposit = async (id, data) => {
  try {
    const response = await api.put(`/depositos/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar depósito:', error);
    throw error;
  }
};

/**
 * Eliminar un depósito
 * @param {string} id - ID del depósito
 * @returns {Promise<void>}
 */
export const deleteDeposit = async (id) => {
  try {
    const response = await api.delete(`/depositos/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar depósito:', error);
    throw error;
  }
};

/**
 * Obtener PDF del comprobante de depósito
 * @param {string} id - ID del depósito
 * @returns {Promise<Blob>} PDF del depósito
 */
export const getDepositPDF = async (id) => {
  try {
    const response = await api.get(`/depositos/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener PDF del depósito:', error);
    throw error;
  }
};

/**
 * Descargar PDF del comprobante de depósito
 * @param {string} id - ID del depósito
 * @param {string} filename - Nombre del archivo a descargar
 */
export const downloadDepositPDF = async (id, filename = null) => {
  try {
    const blob = await getDepositPDF(id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `deposito_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar PDF del depósito:', error);
    throw error;
  }
};

/**
 * Obtener depósitos por cuenta bancaria
 * @param {string} cuentaId - ID de la cuenta bancaria
 * @returns {Promise<Array>} Lista de depósitos
 */
export const getDepositsByCuenta = async (cuentaId) => {
  try {
    const response = await api.get('/depositos', {
      params: { cuentaId },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener depósitos por cuenta:', error);
    throw error;
  }
};

/**
 * Obtener depósitos por rango de fechas
 * @param {string} fechaDesde - Fecha inicio (ISO)
 * @param {string} fechaHasta - Fecha fin (ISO)
 * @returns {Promise<Array>} Lista de depósitos
 */
export const getDepositsByDateRange = async (fechaDesde, fechaHasta) => {
  try {
    const response = await api.get('/depositos', {
      params: { fechaDesde, fechaHasta },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener depósitos por rango de fechas:', error);
    throw error;
  }
};

/**
 * Confirmar un depósito
 * @param {string} id - ID del depósito
 * @returns {Promise<Object>} Depósito confirmado
 */
export const confirmDeposit = async (id) => {
  try {
    const response = await api.patch(`/depositos/${id}/confirmar`);
    return response.data;
  } catch (error) {
    console.error('Error al confirmar depósito:', error);
    throw error;
  }
};

/**
 * Cancelar un depósito
 * @param {string} id - ID del depósito
 * @param {string} motivo - Motivo de cancelación
 * @returns {Promise<Object>} Depósito cancelado
 */
export const cancelDeposit = async (id, motivo) => {
  try {
    const response = await api.patch(`/depositos/${id}/cancelar`, { motivo });
    return response.data;
  } catch (error) {
    console.error('Error al cancelar depósito:', error);
    throw error;
  }
};

export default {
  getAllDeposits,
  getDepositById,
  createDeposit,
  updateDeposit,
  deleteDeposit,
  getDepositPDF,
  downloadDepositPDF,
  getDepositsByCuenta,
  getDepositsByDateRange,
  confirmDeposit,
  cancelDeposit,
};
